'use client'

import { useState, useMemo } from 'react'
import NewMatchCard from './NewMatchCard'
import SportsFilter from './SportsFilter'
import { Match } from '@/lib/database'

interface FilterCounts {
  liveToday: any[]
  liveTomorrow: any[]
  isLive: any[]
  liveTops: any[]
  liveCaster: any[]
  liveAll: any[]
}

interface ClientFilterWrapperProps {
  initialMatches: Match[]
  matchCounts?: {
    total: number
    live: number
    today: number
    tomorrow: number
  }
}

export default function ClientFilterWrapper({ 
  initialMatches, 
  matchCounts 
}: ClientFilterWrapperProps) {
  // State for filtered matches from SportsFilter
  const [filteredMatches, setFilteredMatches] = useState<Match[]>([])
  const [filterCounts, setFilterCounts] = useState<FilterCounts | null>(null)
  const [isUsingFilter, setIsUsingFilter] = useState(false)
  const [isFilterLoading, setIsFilterLoading] = useState(false)

  // Handle filter changes from SportsFilter
  const handleFilterChange = (matches: Match[], counts: FilterCounts) => {
    // Show loading skeleton briefly for smooth transition
    setIsFilterLoading(true)
    
    setTimeout(() => {
      setFilteredMatches(matches)
      setFilterCounts(counts)
      setIsUsingFilter(true)
      setIsFilterLoading(false)
    }, 150) // Brief delay for smooth transition
  }

  // Show filtered matches if filter is active, otherwise show all pre-built matches
  const matches = useMemo(() => {
    if (isUsingFilter && filteredMatches.length > 0) {
      return filteredMatches
    }
    return initialMatches
  }, [isUsingFilter, filteredMatches, initialMatches])

  return (
    <>
      {/* 🚀 SportsFilter with pre-built data */}
      <SportsFilter onFilterChange={handleFilterChange} />
      
      {/* 🚀 Cards - pre-built + client-side filtering */}
      <div className="fs-matches-wrapper">
        <div className="fs-matches-grid">
          {isFilterLoading ? (
            // Show skeleton during filter transition using NewMatchCard built-in skeleton
            [...Array(6)].map((_, i) => (
              <NewMatchCard 
                key={`filter-skeleton-${i}`}
                match={{
                  id: `skeleton-${i}`,
                  team_hone: 'Loading',
                  team_away: 'Loading',
                  league_name: 'Loading',
                  match_status: '1'
                } as any}
                isLoaded={false} // This triggers built-in skeleton
                allMatches={[]}
              />
            ))
          ) : matches.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-400">
              {isUsingFilter ? 'Không có trận đấu nào khớp với bộ lọc' : 'Không có trận đấu nào'}
            </div>
          ) : (
            // 🚀 Sort live matches first for better LCP 
            [...matches]
              .sort((a, b) => {
                const aIsLive = String(a.match_status) === '3'
                const bIsLive = String(b.match_status) === '3'
                if (aIsLive && !bIsLive) return -1
                if (!aIsLive && bIsLive) return 1
                return 0
              })
              .map((match, index) => {
                // 🚀 Prioritize live matches + first 6 cards for LCP
                const isLiveMatch = String(match.match_status) === '3'
                const shouldPrioritize = index < 6 || isLiveMatch
                
                return (
                  <NewMatchCard 
                    key={match.id} 
                    match={match}
                    isLoaded={true}
                    priority={shouldPrioritize} // 🚀 Live matches + first 6 get priority
                    allMatches={matches}
                  />
                )
              })
          )}
        </div>
      </div>

      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-2 bg-blue-900/20 border border-blue-500/30 rounded text-xs text-blue-400">
          <div>🔍 Filter: {isUsingFilter ? 'Active' : 'Showing all'}</div>
          <div>📊 Showing: {matches.length} / {initialMatches.length} cards</div>
          <div>💾 Pre-built: {initialMatches.length} matches</div>
        </div>
      )}
    </>
  )
}
